import { defaultFallbackInView }                                                                                                                                                                     from 'react-intersection-observer';
import React, { FC, MutableRefObject, useContext, useEffect, useState }                                                                                                                              from 'react';
import { Router }                                                                                                                                                                                    from './Router';
import { ToastsHook, useLogging, useModal, useMouseCoordinates, useSidebar, useToasts, useUnsavedDataCheck }                                                                                         from '../hooks';
import { TogglableContentHook }                                                                                                                                                                      from '../hooks/useTogglableContentReducer';
import { DynamicComponentsContext, DynamicContentContextType, SystemDataContext, SystemDataObjects, SystemUtilContext, SystemUtilObjects, ThemeContext, ThemeContextType, UIContext, UIContextType } from './contexts';
import { Page, PageModal }                                                                                                                                                                           from './containers';
import { Toasts }                                                                                                                                                                                    from './components/toasts/Toasts';
import { EnvironmentVisualFlag }                                                                                                                                                                     from './components/product/EnvironmentVisualFlag';
import { InternalBugReportForm, ProductionBugReportForm }                                                                                                                                            from './components/util/external/UserSnapForm';
import { GoogleAnalytics }                                                                                                                                                                           from './components/util/external/GoogleAnalytics';
import { HubSpotAnalytics }                                                                                                                                                                          from './components/util/external/HubSpotAnalytics';
import { Intercom }                                                                                                                                                                                  from './components/util/external/Intercom';
import { LaunchDarkly }                                                                                                                                                                              from './components/util/external/LaunchDarkly';
import { OffScreenNode }                                                                                                                                                                             from './containers/OffScreenNode';
import { HeapIOAnalytics }                                                                                                                                                                           from './components/util/external/HeapIOAnalytics';
import { DataDogRUMIdentifier }                                                                                                                                                                      from './components/util/external/DataDogRUMIdentifier';
import { ENVIRONMENT_IS_DEVELOPMENT }                                                                                                                                                                from '../constants/SystemEnums';
import { usePrefStateResolver }                                                                                                                                                                      from '../hooks/usePrefStateResolver';
import { Auth0Context }                                                                                                                                                                              from '@auth0/auth0-react';
import { useSystemData }                                                                                                                                                                             from '@src/hooks/useSystemData'; // Example path
import { AiProvider }                                                                                                                                                                                from './pages/projects/dash/AiContext';
import { useAiContext }                                                                                                                                                                              from './pages/projects/dash/AiContext';
import DashAIPanel                                                                                                                                                                                   from './pages/projects/dash/DashAIPanel';


// React InView Component:
// If no intersection observer capabilities, just default to everything in view.
defaultFallbackInView( true );


export interface AppProps {
    welcomeUser?: boolean;
}

const DynamicComponentsContextValues: MutableRefObject<DynamicContentContextType | null> = React.createRef(),
      SystemUtilContextValues: MutableRefObject<SystemUtilObjects | null>                = React.createRef(),
      SystemDataContextValues: MutableRefObject<SystemDataObjects | null>                = React.createRef(),
      ThemeContextValues: MutableRefObject<ThemeContextType | null>                      = React.createRef(),
      UIContextValues: MutableRefObject<UIContextType | null>                            = React.createRef();

const DynamicComponentsContextProvider = ( { children } ) => {

    let sidebar: TogglableContentHook = useSidebar(),
        toasts: ToastsHook            = useToasts(),
        modal: TogglableContentHook   = useModal();

    DynamicComponentsContextValues.current = { sidebar, toasts, modal };

    return (
        <DynamicComponentsContext.Provider value={ DynamicComponentsContextValues.current }>

            { children }

        </DynamicComponentsContext.Provider>
    );
};

const SystemUtilContextProvider = ( { children } ) => {

    let logging = useLogging();

    SystemUtilContextValues.current = {
        logging
    };

    return (
        <SystemUtilContext.Provider value={ SystemUtilContextValues.current }>

            { children }

        </SystemUtilContext.Provider>
    );
};

const SystemDataContextProvider = ( { children } ) => {

    let systemData = useSystemData();

    SystemDataContextValues.current = {
        businessFunctions: systemData.businessFunctions,
    };

    return (
        <SystemDataContext.Provider value={ SystemDataContextValues.current }>

            { systemData.loading ? null : children }

        </SystemDataContext.Provider>
    );
};

const UIContextProvider = ( { children } ) => {

    let mouseCoordinates = useMouseCoordinates();

    UIContextValues.current = { mouseCoordinates };

    return (
        <UIContext.Provider value={ UIContextValues.current }>

            { children }

        </UIContext.Provider>
    );
};

const ThemeContextProvider = ( { children } ) => {

    ThemeContextValues.current = {};

    return (
        <ThemeContext.Provider value={ ThemeContextValues.current }>

            { children }

        </ThemeContext.Provider>
    );
};

const App: FC<AppProps> = ( { welcomeUser = false, ...props } ) => {

    const { clearUnsavedDataIds }      = useUnsavedDataCheck(),
          { resolvePreferenceUpdates } = usePrefStateResolver(),
          { isAuthenticated, user }    = useContext( Auth0Context ),
          [ showTour, setShowTour ]    = useState( false );

    const handleFinishTour = () => {
        setShowTour( false ); // Hide the tour once finished
    };

    useEffect( () => {

        // Clear any unsaved data ids
        clearUnsavedDataIds();

        // Resolve preference updates, resetting if necssary
        resolvePreferenceUpdates();

    }, [] );

    return (

        <LaunchDarkly>
            <AiProvider>
                <SystemDataContextProvider>
                    <SystemUtilContextProvider>
                        <UIContextProvider>
                            <ThemeContextProvider>
                                <DynamicComponentsContextProvider>
                                    <AppRoot { ...props } />
                                </DynamicComponentsContextProvider>
                            </ThemeContextProvider>
                        </UIContextProvider>
                    </SystemUtilContextProvider>
                </SystemDataContextProvider>
            </AiProvider>
        </LaunchDarkly>
    );
};

const AppRoot: FC<AppProps> = ( props ) => {

    // Determine children or route based rendering...
    let { children, ...p } = props,
        hasChildren        = !( children === null || children === undefined );
    const { showAi, setShowAi } = useAiContext();

    return (
        <>
            {/* Flag indicating what environment a user is on. */ }
            <EnvironmentVisualFlag/>

            {/* Main Content */ }
            { hasChildren ? <Page showTop={ false } showNav={ false }>{ children }</Page> : <Router/> }

            {/* Generic toast / messaging mechanism */ }
            <Toasts/>

            {/* Generic modal mechanism */ }
            <PageModal/>

            { showAi && <DashAIPanel open={ true } onClose={ () => setShowAi( false ) }/> }

            {/* Used to tap or hold off-screen displays */ }
            <OffScreenNode/>

            {/* Internal Bug Report */ }
            <InternalBugReportForm useVisualBug={ false }
                                   inProduction={ false }
                                   inStaging={ false }
                                   inTesting={ true }
                                   inDevelopment={ false }/>

            {/* Production Bug / Improvement Feedback */ }
            <ProductionBugReportForm useVisualBug={ false }
                                     inProduction={ true }
                                     inStaging={ false }
                                     inTesting={ false }
                                     inDevelopment={ false }/>

            {/* Google Analyics */ }
            <GoogleAnalytics id={ 'UA-178861578-1' }
                             useVisualBug={ false }
                             inProduction={ false }
                             inStaging={ false }
                             inTesting={ true }
                             inDevelopment={ false }/>

            {/* HubSpot Analytics */ }
            <HubSpotAnalytics id={ '8144088' }
                              useVisualBug={ false }
                              inProduction={ true }
                              inStaging={ true }
                              inTesting={ true }
                              inDevelopment={ false }/>

            {/* Intercom */ }
            <Intercom id={ 'yx43miwd' }
                      useVisualBug={ false }
                      inProduction={ true }
                      inStaging={ true }
                      inTesting={ true }
                      inDevelopment={ false }/>

            {/* User Pilot  - [Replacing User Pilot with Itercom]
             <UserPilot id={ '26si72p4' }
             useVisualBug={ false }
             inProduction={ true }
             inStaging={ true }
             inTesting={ true }
             inDevelopment={ true }/>
             */ }

            {/* Heap.io */ }
            <HeapIOAnalytics useVisualBug={ false }
                             inProduction={ true }
                             inStaging={ true }
                             inTesting={ true }
                             inDevelopment={ false }/>

            {/* Data Dog */ }
            { !ENVIRONMENT_IS_DEVELOPMENT && <DataDogRUMIdentifier/> }
        </>
    );
};

export default App;
